import { useState, useRef, useEffect, useCallback } from "react";
import {
  X, RotateCcw, Image as ImageIcon, Zap, ZapOff, SwitchCamera,
  Play, Check, Loader2, Square, Video, Camera, RefreshCw, Plus,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import Cropper, { type Area } from "react-easy-crop";
import { useToast } from "@/hooks/use-toast";

const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;

export interface CapturedMedia {
  type: 'photo' | 'video';
  file: File;
  url: string;
  thumbnailUrl?: string;
  duration?: number;
  gpsCoordinates?: { latitude: number; longitude: number };
}

interface CameraFirstCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (media: CapturedMedia) => void;
  onGallerySelect?: (media: CapturedMedia) => void;
  onSkip?: () => void;
  /** Pre-selected files from the restaurant step gallery picker */
  initialFiles?: CapturedMedia[];
  /** Pre-acquired camera stream from the restaurant step (avoids re-prompting on iOS) */
  initialStream?: MediaStream | null;
}

type PickerTab = 'gallery' | 'camera';
type TopView = 'idle' | 'preview' | 'video' | 'camera-live' | 'camera-error';

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise<void>((resolve) => { image.onload = () => resolve(); });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas toBlob failed'));
    }, 'image/jpeg', 0.92);
  });
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

async function extractVideoThumbnail(videoUrl: string): Promise<string | undefined> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;

    let settled = false;
    const settle = (result: string | undefined) => {
      if (settled) return;
      settled = true;
      video.src = '';
      resolve(result);
    };

    const drawFrame = () => {
      try {
        const w = video.videoWidth || 160;
        const h = video.videoHeight || 160;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { settle(undefined); return; }
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => settle(blob ? URL.createObjectURL(blob) : undefined),
          'image/jpeg',
          0.7,
        );
      } catch {
        settle(undefined);
      }
    };

    video.addEventListener('loadedmetadata', () => {
      video.currentTime = 0.1; // seek just past frame 0 (avoids black frame on some codecs)
    }, { once: true });

    video.addEventListener('seeked', drawFrame, { once: true });
    video.addEventListener('error', () => settle(undefined), { once: true });

    video.src = videoUrl;
    video.load();

    // Fallback timeout so a slow/stuck video never blocks the UI
    setTimeout(() => settle(undefined), 6000);
  });
}

export default function CameraFirstCapture({
  isOpen,
  onClose,
  onCapture,
  onSkip,
  initialFiles,
  initialStream,
}: CameraFirstCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const pressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const MAX_RECORDING_DURATION = 60;
  const { toast } = useToast();

  const [tab, setTab] = useState<PickerTab>('gallery');
  const [topView, setTopView] = useState<TopView>('idle');

  const [selectedFiles, setSelectedFiles] = useState<CapturedMedia[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeMedia, setActiveMedia] = useState<CapturedMedia | null>(null);

  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);
  const [captureMode, setCaptureMode] = useState<'photo' | 'video'>('photo');

  const [isRecording, setIsRecording] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isPressing, setIsPressing] = useState(false);

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const resetAll = useCallback(() => {
    setTab('gallery');
    setTopView('idle');
    setSelectedFiles([]);
    setActiveIndex(0);
    setActiveMedia(null);
    setCameraError(null);
    setIsPermissionDenied(false);
    setIsInitializing(false);
    setIsRecording(false);
    setRecordingProgress(0);
    setRecordingSeconds(0);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setFlashEnabled(false);
    setIsPressing(false);
    setIsVideoPlaying(false);
    setCaptureMode('photo');
    setHasTorch(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopStream();
      selectedFiles.forEach((f) => URL.revokeObjectURL(f.url));
      resetAll();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => stopStream();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply pre-acquired stream (from restaurant step camera button) — satisfies iOS gesture requirement
  const initialStreamAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialStream || !isOpen || initialStreamAppliedRef.current) return;
    initialStreamAppliedRef.current = true;
    streamRef.current = initialStream;
    setTab('camera');
    setIsInitializing(false);
    setTopView('camera-live');
    if (videoRef.current) {
      videoRef.current.srcObject = initialStream;
      videoRef.current.play().catch(() => {});
    }
    const track = initialStream.getVideoTracks()[0];
    const caps = track?.getCapabilities?.() as any;
    setHasTorch(!!caps?.torch);
  }, [initialStream, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply pre-selected files (from restaurant step gallery picker)
  const initialFilesAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialFiles?.length || !isOpen || initialFilesAppliedRef.current) return;
    initialFilesAppliedRef.current = true;
    setSelectedFiles(initialFiles);
    const first = initialFiles[0];
    setActiveIndex(0);
    setActiveMedia(first);
    setTopView(first.type === 'video' ? 'video' : 'preview');
    setTab('gallery');
    // Generate video thumbnails async
    initialFiles.forEach(async (m) => {
      if (m.type !== 'video') return;
      const thumbnailUrl = await extractVideoThumbnail(m.url);
      if (!thumbnailUrl) return;
      setSelectedFiles((prev) => prev.map((f) => (f.url === m.url ? { ...f, thumbnailUrl } : f)));
    });
  }, [initialFiles, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeCamera = useCallback(async () => {
    setCameraError(null);
    setIsPermissionDenied(false);
    setIsInitializing(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera not supported in this browser.');
      setTopView('camera-error');
      setIsInitializing(false);
      return;
    }

    const videoConstraints = { width: { ideal: 1920 }, height: { ideal: 1080 } };
    const attempts: MediaStreamConstraints[] = [
      { video: { facingMode, ...videoConstraints }, audio: true },
      ...(facingMode === 'environment'
        ? [{ video: { facingMode: 'user' as const, ...videoConstraints }, audio: true }]
        : []),
      { video: videoConstraints, audio: true },
      { video: true, audio: true },
    ];

    for (const constraints of attempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const track = stream.getVideoTracks()[0];
        const caps = track.getCapabilities?.() as any;
        setHasTorch(!!caps?.torch);
        setIsInitializing(false);
        setTopView('camera-live');
        return;
      } catch (err) {
        const name = err instanceof Error ? err.name : '';
        const isDeviceError =
          name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError';
        if (isDeviceError) continue;

        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setIsPermissionDenied(true);
          setCameraError('Camera access was denied. Allow access in your browser settings, then tap "Try again".');
          setIsInitializing(false);
          setTopView('camera-error');
          return;
        }

        const msg =
          name === 'NotReadableError' || name === 'TrackStartError'
            ? 'Camera is in use by another app. Close it and try again.'
            : name === 'SecurityError'
            ? 'Camera blocked by browser security settings.'
            : 'Unable to access camera.';
        setCameraError(msg);
        setIsInitializing(false);
        setTopView('camera-error');
        return;
      }
    }

    setCameraError('No camera found on this device.');
    setIsInitializing(false);
    setTopView('camera-error');
  }, [facingMode]);

  const handleCameraTab = useCallback(() => {
    if (tab === 'camera') return;
    setActiveMedia(null);
    setIsVideoPlaying(false);
    setTab('camera');
    initializeCamera();
  }, [tab, initializeCamera]);

  const handleGalleryTab = useCallback(() => {
    if (tab === 'gallery') return;
    stopStream();
    setTab('gallery');
    setActiveMedia(null);
    if (selectedFiles.length > 0) {
      const m = selectedFiles[activeIndex] ?? selectedFiles[0];
      setActiveMedia(m);
      setTopView(m.type === 'video' ? 'video' : 'preview');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } else {
      setTopView('idle');
    }
  }, [tab, stopStream, selectedFiles, activeIndex]);

  useEffect(() => {
    if (tab === 'camera' && topView === 'camera-live' && !isInitializing) {
      stopStream();
      initializeCamera();
    }
  }, [facingMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const flipCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  }, []);

  const toggleFlash = useCallback(() => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    if (!track) return;
    const next = !flashEnabled;
    track.applyConstraints({ advanced: [{ torch: next } as any] }).catch(() => {});
    setFlashEnabled(next);
  }, [flashEnabled]);

  const handleGalleryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const newMedia: CapturedMedia[] = [];
    let hasOversizeVideo = false;

    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      if (isVideo && file.size > MAX_VIDEO_SIZE_BYTES) {
        hasOversizeVideo = true;
        continue;
      }
      newMedia.push({
        type: isVideo ? 'video' : 'photo',
        file,
        url: URL.createObjectURL(file),
      });
    }

    if (hasOversizeVideo) {
      toast({
        title: 'Video too large',
        description: 'Some videos exceeded 100 MB and were skipped. Please choose shorter clips.',
        variant: 'destructive',
      });
    }

    if (!newMedia.length) {
      e.target.value = '';
      return;
    }

    setSelectedFiles((prev) => {
      const combined = [...prev, ...newMedia];
      const newIdx = prev.length;
      setActiveIndex(newIdx);
      const m = combined[newIdx];
      setActiveMedia(m);
      setTopView(m.type === 'video' ? 'video' : 'preview');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setIsVideoPlaying(false);
      if (tab !== 'gallery') {
        stopStream();
        setTab('gallery');
      }
      return combined;
    });

    // Generate poster-frame thumbnails for video files asynchronously.
    // Files are already visible in the strip; thumbnail URL is patched in once ready.
    newMedia.forEach(async (m) => {
      if (m.type !== 'video') return;
      const thumbnailUrl = await extractVideoThumbnail(m.url);
      if (!thumbnailUrl) return;
      setSelectedFiles((prev) =>
        prev.map((f) => (f.url === m.url ? { ...f, thumbnailUrl } : f)),
      );
      setActiveMedia((prev) =>
        prev?.url === m.url ? { ...prev, thumbnailUrl } : prev,
      );
    });

    e.target.value = '';
  }, [tab, stopStream, toast]);

  const handleSelectThumbnail = useCallback((idx: number) => {
    setActiveIndex(idx);
    const m = selectedFiles[idx];
    setActiveMedia(m);
    setTopView(m.type === 'video' ? 'video' : 'preview');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setIsVideoPlaying(false);
  }, [selectedFiles]);

  const takePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video || !streamRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `review-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      setActiveMedia({ type: 'photo', file, url });
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setTopView('preview');
    }, 'image/jpeg', 0.9);
  }, [facingMode]);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    recordingStartTimeRef.current = Date.now();

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';

    const recorder = new MediaRecorder(streamRef.current, { mimeType, videoBitsPerSecond: 2_500_000 });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const duration = (Date.now() - recordingStartTimeRef.current) / 1000;
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `review-video-${Date.now()}.${ext}`, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setActiveMedia({ type: 'video', file, url, duration });
      setIsRecording(false);
      setRecordingProgress(0);
      setRecordingSeconds(0);
      setTopView('video');
    };

    mediaRecorderRef.current = recorder;
    recorder.start(100);
    setIsRecording(true);

    recordingTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - recordingStartTimeRef.current) / 1000;
      setRecordingSeconds(elapsed);
      setRecordingProgress(Math.min((elapsed / MAX_RECORDING_DURATION) * 100, 100));
      if (elapsed >= MAX_RECORDING_DURATION) stopRecordingFn();
    }, 100);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecordingFn = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const handleShutterPress = useCallback(() => {
    setIsPressing(true);
    if (captureMode === 'photo') return;
    pressTimerRef.current = setTimeout(() => { startRecording(); }, 150);
  }, [captureMode, startRecording]);

  const handleShutterRelease = useCallback(() => {
    setIsPressing(false);
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  }, []);

  const handleShutterTap = useCallback(() => {
    if (captureMode === 'photo') {
      takePhoto();
    } else if (!isRecording) {
      startRecording();
    }
  }, [captureMode, takePhoto, startRecording, isRecording]);

  const handleRetake = useCallback(() => {
    // Only revoke the URL if it's a camera capture (not a gallery item — those URLs
    // are still referenced by the thumbnail strip and must not be revoked).
    const isGalleryItem = selectedFiles.some((f) => f.url === activeMedia?.url);
    if (activeMedia?.url && !isGalleryItem) URL.revokeObjectURL(activeMedia.url);
    setActiveMedia(null);
    setIsVideoPlaying(false);
    if (tab === 'camera') {
      setTopView('camera-live');
      if (streamRef.current && videoRef.current) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(() => {});
      }
    } else {
      // Gallery retake: return to idle so the user can select a different file
      // from the strip or open the picker again. Don't re-display the same item.
      setTopView('idle');
    }
  }, [activeMedia, tab, selectedFiles]);

  const handleConfirmPhoto = useCallback(async () => {
    if (!activeMedia || activeMedia.type !== 'photo') return;
    setIsCropping(true);
    try {
      let file = activeMedia.file;
      let url = activeMedia.url;
      if (croppedAreaPixels) {
        const blob = await getCroppedImg(activeMedia.url, croppedAreaPixels);
        file = new File([blob], activeMedia.file.name, { type: 'image/jpeg' });
        url = URL.createObjectURL(blob);
        URL.revokeObjectURL(activeMedia.url);
      }
      onCapture({ ...activeMedia, file, url });
    } finally {
      setIsCropping(false);
    }
  }, [activeMedia, croppedAreaPixels, onCapture]);

  const handleConfirmVideo = useCallback(() => {
    if (activeMedia) onCapture(activeMedia);
  }, [activeMedia, onCapture]);

  const handleClose = useCallback(() => {
    selectedFiles.forEach((f) => {
      URL.revokeObjectURL(f.url);
      if (f.thumbnailUrl) URL.revokeObjectURL(f.thumbnailUrl);
    });
    if (activeMedia?.url && !selectedFiles.some((f) => f.url === activeMedia.url)) {
      URL.revokeObjectURL(activeMedia.url);
    }
    stopStream();
    stopRecordingFn();
    onClose();
  }, [selectedFiles, activeMedia, stopStream, stopRecordingFn, onClose]);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const showGalleryStrip = tab === 'gallery' && selectedFiles.length > 0 && topView === 'idle';
  const showCameraViewfinder =
    tab === 'camera' && (topView === 'camera-live' || isInitializing || isRecording);
  const showPhotoPreview = topView === 'preview' && activeMedia?.type === 'photo';
  const showVideoPreview = topView === 'video' && activeMedia?.type === 'video';
  const showCameraError = tab === 'camera' && topView === 'camera-error' && !isInitializing;
  const showCameraControls =
    tab === 'camera' && (topView === 'camera-live' || isInitializing) && !isRecording;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
        {/* ─── TOP PREVIEW / VIEWFINDER ─── */}
        <div className="relative flex-1 overflow-hidden bg-black min-h-0">

          {/* Persistent top bar */}
          <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-3 pt-3 pb-6 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
            <div className="pointer-events-auto">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="text-white hover:bg-white/20 rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="pointer-events-auto">
              {hasTorch && showCameraViewfinder && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleFlash}
                  className="text-white hover:bg-white/20 rounded-full"
                >
                  {flashEnabled
                    ? <Zap className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    : <ZapOff className="w-5 h-5" />
                  }
                </Button>
              )}
            </div>
          </div>

          {/* IDLE PLACEHOLDER */}
          {topView === 'idle' && !showCameraViewfinder && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-8">
              <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
                <ImageIcon className="w-10 h-10 text-white/40" />
              </div>
              <div className="text-center">
                <p className="text-white/70 font-medium text-base">Select photos or videos</p>
                <p className="text-white/40 text-sm mt-1">
                  Tap <span className="text-white/60">Gallery</span> to choose from your library, or{' '}
                  <span className="text-white/60">Camera</span> to take a new one
                </p>
              </div>
            </div>
          )}

          {/* LIVE CAMERA VIEWFINDER (always mounted so stream attaches immediately) */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn(
              "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
              showCameraViewfinder ? "opacity-100" : "opacity-0 pointer-events-none",
              facingMode === 'user' && "scale-x-[-1]",
            )}
          />

          {/* Camera initializing spinner */}
          {tab === 'camera' && isInitializing && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black gap-3 z-10">
              <Loader2 className="w-9 h-9 text-white animate-spin" />
              <span className="text-white/60 text-sm">Starting camera…</span>
            </div>
          )}

          {/* Camera error */}
          {showCameraError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black gap-4 px-8 text-center z-10">
              <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                {isPermissionDenied
                  ? <Camera className="w-7 h-7 text-red-400" />
                  : <X className="w-7 h-7 text-red-400" />
                }
              </div>
              <div className="space-y-1.5">
                <p className="text-white/90 font-medium">
                  {isPermissionDenied ? 'Camera Access Denied' : 'Camera Unavailable'}
                </p>
                <p className="text-white/50 text-sm">{cameraError}</p>
              </div>
            </div>
          )}

          {/* Recording timer badge */}
          {isRecording && (
            <div className="absolute top-14 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm border border-white/20">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-white text-sm font-mono font-medium tabular-nums">
                {formatDuration(recordingSeconds)}
              </span>
            </div>
          )}

          {/* Photo crop editor */}
          {showPhotoPreview && (
            <Cropper
              image={activeMedia!.url}
              crop={crop}
              zoom={zoom}
              aspect={4 / 3}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid
              style={{
                containerStyle: { position: 'absolute', inset: 0 },
                mediaStyle: {},
                cropAreaStyle: {},
              }}
            />
          )}

          {/* Video player */}
          {showVideoPreview && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={() => {
                if (previewVideoRef.current) {
                  if (isVideoPlaying) previewVideoRef.current.pause();
                  else previewVideoRef.current.play();
                  setIsVideoPlaying(!isVideoPlaying);
                }
              }}
            >
              <video
                ref={previewVideoRef}
                src={activeMedia!.url}
                className="w-full h-full object-contain"
                loop
                playsInline
                onEnded={() => setIsVideoPlaying(false)}
              />
              {!isVideoPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                    <Play className="w-7 h-7 text-white ml-1 fill-white" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ─── GALLERY THUMBNAIL STRIP ─── */}
        {showGalleryStrip && (
          <div className="bg-black border-t border-white/10 py-2 px-3 flex-shrink-0">
            <div
              className="flex gap-2 overflow-x-auto"
              style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
            >
              {selectedFiles.map((file, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectThumbnail(idx)}
                  className={cn(
                    "relative flex-shrink-0 w-[72px] h-[72px] rounded-md overflow-hidden border-2 transition-all",
                    activeIndex === idx ? "border-teal-400" : "border-transparent opacity-70 hover:opacity-90",
                  )}
                >
                  {file.type === 'video' && file.thumbnailUrl ? (
                    <img src={file.thumbnailUrl} alt="" className="w-full h-full object-cover" draggable={false} />
                  ) : file.type === 'video' ? (
                    <div className="w-full h-full bg-white/10 flex items-center justify-center">
                      <Video className="w-6 h-6 text-white/40 animate-pulse" />
                    </div>
                  ) : (
                    <img src={file.url} alt="" className="w-full h-full object-cover" draggable={false} />
                  )}
                  {file.type === 'video' && (
                    <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-black/60 flex items-center justify-center">
                      <Play className="w-2.5 h-2.5 text-white fill-white ml-0.5" />
                    </div>
                  )}
                </button>
              ))}

              {/* Add more */}
              <label htmlFor="gallery-input" className="flex-shrink-0 cursor-pointer">
                <div className="w-[72px] h-[72px] rounded-md border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-white/40 hover:border-white/40 hover:text-white/60 transition-colors">
                  <Plus className="w-5 h-5" />
                  <span className="text-[10px]">Add</span>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* ─── TAB BAR ─── */}
        <div className="bg-black border-t border-white/10 flex items-center justify-center gap-12 py-3 flex-shrink-0">
          <button
            onClick={() => {
              // Always open the file picker; stop camera stream first if needed.
              if (tab === 'camera') {
                handleGalleryTab(); // stops stream, restores any existing gallery selection
              }
              galleryInputRef.current?.click();
            }}
            className={cn(
              "flex flex-col items-center gap-1 transition-all active:opacity-60",
              tab === 'gallery' ? "text-white" : "text-white/40",
            )}
          >
            <ImageIcon className="w-6 h-6" />
            <span className="text-[10px] font-medium">Gallery</span>
          </button>
          <button
            onClick={handleCameraTab}
            className={cn(
              "flex flex-col items-center gap-1 transition-all active:opacity-60",
              tab === 'camera' ? "text-white" : "text-white/40",
            )}
          >
            <Camera className="w-6 h-6" />
            <span className="text-[10px] font-medium">Camera</span>
          </button>
        </div>

        {/* ─── BOTTOM CONTROLS ─── */}
        <div className="bg-black flex-shrink-0 px-4 py-3 flex flex-col items-center min-h-[110px]" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div>

            {/* IDLE / GALLERY EMPTY */}
            {topView === 'idle' && tab === 'gallery' && (
              <div className="flex flex-col items-center gap-3 w-full">
                <label htmlFor="gallery-input" className="w-full max-w-xs cursor-pointer">
                  <Button
                    className="w-full rounded-full bg-primary text-primary-foreground pointer-events-none"
                    tabIndex={-1}
                    asChild
                  >
                    <span>
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Choose from Gallery
                    </span>
                  </Button>
                </label>
                {onSkip && (
                  <button
                    onClick={onSkip}
                    className="text-white/40 text-xs underline underline-offset-2 hover:text-white/60 transition-colors"
                  >
                    Skip — write review without photo
                  </button>
                )}
              </div>
            )}

            {/* PHOTO PREVIEW / CROP CONTROLS */}
            {showPhotoPreview && (
              <div className="flex flex-col gap-3 w-full max-w-sm">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60 text-xs">Zoom</span>
                    <button
                      onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(1); }}
                      className="text-white/40 text-xs flex items-center gap-1 hover:text-white/70 transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reset
                    </button>
                  </div>
                  <Slider
                    value={[zoom]}
                    onValueChange={(v) => setZoom(v[0])}
                    min={1}
                    max={3}
                    step={0.05}
                    className="w-full"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handleRetake}
                    className="flex-1 rounded-full border-white/30 text-white bg-white/10"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Retake
                  </Button>
                  <Button
                    onClick={handleConfirmPhoto}
                    disabled={isCropping}
                    className="flex-1 rounded-full bg-primary text-primary-foreground"
                  >
                    {isCropping
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Check className="w-4 h-4 mr-2" />
                    }
                    Use This
                  </Button>
                </div>
              </div>
            )}

            {/* VIDEO PREVIEW CONTROLS */}
            {showVideoPreview && (
              <div className="flex flex-col items-center gap-3 w-full max-w-sm">
                {activeMedia?.type === 'video' && (() => {
                  const sizeMB = activeMedia.file.size / (1024 * 1024);
                  const isOverLimit = activeMedia.file.size > MAX_VIDEO_SIZE_BYTES;
                  return isOverLimit ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-destructive/80 text-white text-xs">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{sizeMB.toFixed(1)} MB — exceeds 100 MB limit. Please retake.</span>
                    </div>
                  ) : (
                    <div className="text-white/60 text-xs">{sizeMB.toFixed(1)} MB / 100 MB max</div>
                  );
                })()}
                <div className="flex items-center gap-4 w-full">
                  <Button
                    variant="outline"
                    onClick={handleRetake}
                    className="flex-1 rounded-full border-white/30 text-white bg-white/10"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Retake
                  </Button>
                  <Button
                    onClick={handleConfirmVideo}
                    disabled={activeMedia?.type === 'video' && activeMedia.file.size > MAX_VIDEO_SIZE_BYTES}
                    className="flex-1 rounded-full bg-primary text-primary-foreground"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Use This
                  </Button>
                </div>
              </div>
            )}

            {/* LIVE CAMERA CONTROLS */}
            {showCameraControls && (
              <div className="flex flex-col items-center gap-4 w-full">
                {/* Photo / Video mode tabs */}
                <div className="flex items-center gap-1 bg-white/10 rounded-full p-1">
                  <button
                    onClick={() => setCaptureMode('photo')}
                    className={cn(
                      "flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200",
                      captureMode === 'photo' ? "bg-white text-black" : "text-white/70",
                    )}
                  >
                    <Camera className="w-3.5 h-3.5" />
                    Photo
                  </button>
                  <button
                    onClick={() => setCaptureMode('video')}
                    className={cn(
                      "flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200",
                      captureMode === 'video' ? "bg-white text-black" : "text-white/70",
                    )}
                  >
                    <Video className="w-3.5 h-3.5" />
                    Video
                  </button>
                </div>

                {/* Shutter row */}
                <div className="flex items-center justify-center gap-10 w-full">
                  {/* Gallery shortcut */}
                  <label htmlFor="gallery-input" className="cursor-pointer">
                    <span className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white transition-opacity active:opacity-60">
                      <ImageIcon className="w-5 h-5" />
                    </span>
                  </label>

                  {/* Shutter */}
                  <div className="relative">
                    {captureMode === 'video' && isPressing && (
                      <svg className="absolute -inset-2 w-24 h-24 animate-pulse" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(239,68,68,0.5)" strokeWidth="4" />
                      </svg>
                    )}
                    <button
                      onMouseDown={handleShutterPress}
                      onMouseUp={handleShutterRelease}
                      onMouseLeave={handleShutterRelease}
                      onTouchStart={handleShutterPress}
                      onTouchEnd={handleShutterRelease}
                      onClick={handleShutterTap}
                      disabled={isInitializing}
                      className={cn(
                        "w-20 h-20 rounded-full border-4 border-white transition-all duration-150 focus:outline-none",
                        captureMode === 'video'
                          ? isPressing ? "bg-red-500/60 scale-95" : "bg-red-500/20"
                          : isPressing ? "bg-white/40 scale-95" : "bg-white/20",
                        isInitializing && "opacity-40 cursor-not-allowed",
                      )}
                    >
                      {captureMode === 'video' && (
                        <div className="w-5 h-5 mx-auto rounded-full bg-red-500" />
                      )}
                    </button>
                  </div>

                  {/* Flip */}
                  <button
                    onClick={flipCamera}
                    disabled={isInitializing}
                    className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white transition-opacity active:opacity-60 disabled:opacity-30"
                  >
                    <SwitchCamera className="w-5 h-5" />
                  </button>
                </div>

                {!isInitializing && (
                  <p className="text-white/40 text-xs text-center">
                    {captureMode === 'photo' ? 'Tap to take a photo' : 'Tap to record · up to 100 MB'}
                  </p>
                )}
              </div>
            )}

            {/* RECORDING CONTROLS */}
            {isRecording && (
              <div className="flex flex-col items-center gap-3 w-full fm-fade-scale-in">
                <div className="relative">
                  <svg className="absolute -inset-3 w-28 h-28 pointer-events-none" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
                    <circle
                      cx="50" cy="50" r="46"
                      fill="none" stroke="#ef4444" strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={`${recordingProgress * 2.89} 289`}
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                  <button
                    onClick={stopRecordingFn}
                    onTouchStart={(e) => { e.preventDefault(); stopRecordingFn(); }}
                    className="w-[4.5rem] h-[4.5rem] rounded-full bg-red-500 border-4 border-white flex items-center justify-center focus:outline-none"
                  >
                    <Square className="w-5 h-5 text-white fill-white" />
                  </button>
                </div>
                <p className="text-white/50 text-xs">Tap to stop</p>
              </div>
            )}

            {/* CAMERA ERROR CONTROLS */}
            {showCameraError && (
              <div className="flex flex-col items-center gap-4 w-full fm-fade-up">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => initializeCamera()}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 border border-white/25 text-white text-sm font-medium active:opacity-60"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try again
                  </button>
                  <label htmlFor="gallery-input" className="cursor-pointer">
                    <span className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-white/70 text-sm active:opacity-60">
                      <ImageIcon className="w-4 h-4" />
                      Gallery
                    </span>
                  </label>
                </div>
                {onSkip && (
                  <button
                    onClick={onSkip}
                    className="text-white/40 text-xs underline underline-offset-2 hover:text-white/60 transition-colors"
                  >
                    Skip — write review without photo
                  </button>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Hidden gallery file input */}
        <input
          id="gallery-input"
          ref={galleryInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={handleGalleryChange}
          className="sr-only"
        />
    </div>
  );
}
